import { Box, Dialog, DialogTitle, DialogActions, Button } from "@mui/material";
import React, { useCallback, useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Session from "../../../../appfw/authentication/Session.ts";
import { SessionContext } from "../../../../appfw/authentication/SessionProvider.tsx";
import { useW2d2dPod } from "../../hooks/useW2d2dPod.ts";
import { useLinkedResource } from "../../hooks/use-linked-resource.ts";
import { ResourceWrapper } from "../../../../eventer-pod/client/ResourceWrapper.ts";
import { w2d2dApiClient, Idea } from "../../../w2d2d-api-client.ts";
import ParticipationWidget from "../../components/ideas/participation-widget.tsx";
import Event from "../../components/events/event.tsx";
import ShareDialog from "../../../../appfw/share/ShareDialog.tsx";
import { Share, Edit, Delete, Campaign } from "@mui/icons-material";
import { W2d2dNavigation } from "../../../W2d2dNavigation.tsx";
import { useAppFwContextMenuActions } from "../../../../appfw/ui/context-menu/app-fw-context-menu.tsx";
import { encodeResourceUri } from "../../../../eventer/utils/uri-encoding.ts";
import { useIdeasControl } from "../../hooks/useIdeasControl.tsx";
import { getPodNameFromUri } from "../../../ideas/idea-uri-utils";

export interface EventIdeaViewProps {
    event: ResourceWrapper;
    idea: Idea | null;
}

export default function EventIdeaView({ event, idea }: EventIdeaViewProps) {
    const session = useContext<undefined | null | Session>(SessionContext);
    const pod = useW2d2dPod()
    const navigate = useNavigate();
    const location = useLocation();

    // Load linked resources
    const image = useLinkedResource(pod, session, event, 'https://schema.org/image');
    const locationResource = useLinkedResource(pod, session, event, 'https://schema.org/location');

    const [shareData, setShareData] = useState<undefined | ShareData>();
    const [openConfirmDeletionDialog, setOpenConfirmDeletionDialog] = useState(false);

    // Determine if event can be shared
    const canShare = useCallback(() => {
        if (!session?.user || !event) return false;
        // No idea = public event, can share
        if (!idea) return true;
        // Public event (has eventUri) = can share
        if (idea.eventUri) return true;
        // User is author = can share
        if (idea.authorId === session.user.id) return true;
        return false;
    }, [session, event, idea]);

    const isAuthor = idea?.authorId && session?.user?.id && idea.authorId === session.user.id;

    const handleShare = useCallback(async () => {
        if (!event) return;

        if (shareData !== undefined) {
            return;
        }

        const eventName = event.getFirstString('https://schema.org/name') || 'Veranstaltung';
        let shareUrl: string;
        let shareTitle: string;
        let shareText: string;

        // Determine the correct share behavior based on conditions
        if (idea) {
            // Has idea -> use "join" behavior (generate share token)
            shareTitle = "Idee teilen";
            shareText = `Bist du dabei? \n${eventName}`;
            if (idea.authorId) {
                // idea for a private event, generate share token
                const response = await w2d2dApiClient.shareIdea({ id: idea.id });
                shareUrl = W2d2dNavigation.ideas.idea(idea.id).join.token(response.token).absoluteUrl;
            } else {
                // idea for a public event
                shareUrl = W2d2dNavigation.ideas.idea(idea.id).absoluteUrl;
            }
        } else {
            // No idea -> public event, use "share" behavior (direct link)
            const encodedUri = encodeResourceUri(event.subject);
            shareUrl = W2d2dNavigation.events.event(encodedUri).absoluteUrl;
            shareTitle = eventName;
            shareText = `Schau dir diese Veranstaltung an: ${eventName}`;
        }

        setShareData({
            title: shareTitle,
            text: shareText,
            url: shareUrl,
        });
    }, [event, idea, shareData]);

    const handleEdit = useCallback(() => {
        if (!idea) return;
        W2d2dNavigation.ideas.idea(idea.id).edit.navigate(navigate, location);
    }, [idea, navigate, location]);

    const handleDelete = useCallback(() => {
        setOpenConfirmDeletionDialog(true);
    }, []);

    const { removeIdea } = useIdeasControl();

    const confirmDelete = useCallback(async () => {
        if (!idea) return;
        await w2d2dApiClient.deleteIdea({ id: idea.id });
        removeIdea(idea.id);
        setOpenConfirmDeletionDialog(false);
        W2d2dNavigation.ideas.navigate(navigate, location);
    }, [idea, navigate, location, removeIdea]);

    const memoizedActions = React.useMemo(() => {
        const actions = [];

        if (canShare()) {
            actions.push({
                text: 'Teilen',
                icon: <Share fontSize="small" />,
                onClick: handleShare
            });
        }

        if (isAuthor) {
            actions.push({
                text: 'Benachrichtigen',
                icon: <Campaign fontSize="small" />,
                onClick: () => {
                    if (!idea) return;
                    W2d2dNavigation.ideas.idea(idea.id).announce.navigate(navigate, location);
                }
            });

            actions.push({
                text: 'Bearbeiten',
                icon: <Edit fontSize="small" />,
                onClick: handleEdit
            });
            actions.push({
                text: 'Löschen',
                icon: <Delete fontSize="small" />,
                onClick: handleDelete
            });
        }

        return actions;
    }, [canShare, handleShare, isAuthor, handleEdit, handleDelete]);

    useAppFwContextMenuActions(memoizedActions)

    return (
        <>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100dvh',
                overflow: 'hidden',
            }}>
                {/* Scrollable content area */}
                <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    pb: session?.user ? '100px' : 0,
                }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        p: 2,
                        pb: 2,
                    }}>
                        <Event event={event} image={image} location={locationResource} idea={idea} mode="detail" remotePod={getPodNameFromUri(event.subject)} />
                    </Box>
                </Box>

                {/* Sticky Participation Widget */}
                {session?.user && (
                    <ParticipationWidget user={session.user} event={event} idea={idea} />
                )}
            </Box>

            <ShareDialog
                data={shareData}
                onClose={() => setShareData(undefined)}
            />

            <Dialog
                open={openConfirmDeletionDialog}
                onClose={() => setOpenConfirmDeletionDialog(false)}
                aria-labelledby="delete-dialog-title"
            >
                <DialogTitle id="delete-dialog-title">
                    {`Idee wirklich löschen?`}
                </DialogTitle>
                <DialogActions>
                    <Button onClick={() => setOpenConfirmDeletionDialog(false)} autoFocus>Abbrechen</Button>
                    <Button onClick={confirmDelete} color="error">
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
